import React, { FC, useMemo, useState } from 'react';
import { Box, Button, Dialog, Grid, IconButton, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import BankingService from 'services/banking.service';
import { useToasts } from 'hooks/toasts';
import { Close } from '@mui/icons-material';
import { IWithdrawalRequest } from 'common/withdrawal-request.interface';
import { format } from 'date-fns';

export interface ManualWithdrawViewRow {
  id: number;
  status: string;
  value: string;
  createdAt: string;
  originAccount: any;
  accountHolder: string;
  counterpartyName: string;
  counterpartyAccount: any;
  commentary: string;
  type: string;
}

interface ManualWithdrawViewDialogProps {
  open: boolean;
  row?: IWithdrawalRequest;
  source: 'status' | 'eye' | null;
  onClose: () => void;
  onRefresh: () => void;
}

const RowLabelStyle = {
  fontSize: '16px',
  fontWeight: 500,
  color: '#787776',
};

const RowValueStyle = {
  fontSize: '16px',
  fontWeight: 500,
  color: '#4E4D4D',
};

const InfoRow = ({ label, value }: { label: string; value?: string }) => (
  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
    <Typography sx={RowLabelStyle}>{label}</Typography>
    <Typography sx={RowValueStyle}>{value || '-'}</Typography>
  </Box>
);

const WithdrawalReviewModal: FC<ManualWithdrawViewDialogProps> = ({ open, row, source, onClose, onRefresh }) => {
  const theme = useTheme();
  const { triggerToast } = useToasts();
  const bankingService = useMemo(() => new BankingService(), []);

  const [loading, setLoading] = useState(false);

  const handleApprove = async () => {
    if (!row?.id) return;

    setLoading(true);

    try {
      await bankingService.approveWithdrawalRequest(row.id, row.commentary);
      onClose();
      onRefresh();
    } catch {
      triggerToast('Erro ao aprovar solicitação.', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleReject = async () => {
    if (!row?.id) return;

    setLoading(true);

    try {
      await bankingService.rejectWithdrawalRequest(row.id, row.commentary);
      onClose();
      onRefresh();
    } catch {
      triggerToast('Erro ao reprovar solicitação.', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const formattedCreatedBy = useMemo(() => {
    if (!row?.createdAt) return '-';
    const date = new Date(row.createdAt);
    const dateString = format(date, 'dd/MM/yyyy - HH:mm');
    return `${row.createdBy?.email} | ${dateString}`;
  }, [row?.createdAt, row?.createdBy]);

  const formattedApprovedBy = useMemo(() => {
    if (!row?.updatedAt) return '-';
    const date = new Date(row.updatedAt);
    const dateString = format(date, 'dd/MM/yyyy - HH:mm');
    return `${row.approvedBy?.email} | ${dateString}`;
  }, [row?.updatedAt, row?.approvedBy]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      scroll="paper"
      PaperProps={{
        sx: {
          width: 673,
          maxHeight: '90vh',
          borderRadius: '16px',
        },
      }}
    >
      <IconButton
        sx={{
          position: 'absolute',
          top: 15,
          right: 15,
          '@media print': {
            display: 'none',
          },
        }}
        onClick={() => onClose && onClose()}
      >
        <Close />
      </IconButton>
      <Box
        sx={{
          p: '30px 20px',
          display: 'flex',
          flexDirection: 'column',
          gap: '44px',
        }}
      >
        <Typography variant="h6" fontSize={20} fontWeight={600} textAlign="center">
          Detalhe
        </Typography>

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <InfoRow label="Inclusão" value={formattedCreatedBy} />
          <InfoRow label="Revisão" value={formattedApprovedBy} />
          <InfoRow label="Comentário" value={row?.commentary} />
        </Box>

        {source === 'status' && (
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <Button
                fullWidth
                variant="outlined"
                onClick={handleReject}
                disabled={loading}
                sx={{
                  background: 'none',
                  color: theme.palette.primary.main,
                  borderColor: theme.palette.primary.main,
                  textTransform: 'none',
                  fontSize: '14px',
                  fontWeight: '600',
                }}
              >
                Reprovar
              </Button>
            </Grid>

            <Grid item xs={12} md={6}>
              <Button
                sx={{
                  backgroundColor: theme.palette.primary.main,
                  color: '#fff',
                  border: 'none',
                  textTransform: 'none',
                  fontSize: '14px',
                  fontWeight: 600,
                }}
                fullWidth
                variant="contained"
                disabled={loading}
                onClick={handleApprove}
              >
                Aprovar
              </Button>
            </Grid>
          </Grid>
        )}
      </Box>
    </Dialog>
  );
};

export default WithdrawalReviewModal;
